import { decodeHtmlEntities } from '../../../utils/text';
import type { StagedFieldSource } from '../../../utils/stagedFieldSource';
import { toDate } from './productDraft';
import type { AdminCanonicalProduct, Timestampish } from './productDetailTypes';

export const money = (value: number | null | undefined, currency = 'USD'): string =>
  value == null || !Number.isFinite(value)
    ? '—'
    : new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value);

export const formatDate = (value: Timestampish | Date): string => {
  const date = toDate(value as Timestampish);
  return date ? date.toLocaleDateString('en-US') : '';
};

/** Calendar date in UTC, so a sheet window stored at midnight UTC keeps the day the sheet named. */
export const formatUtcDate = (value: Timestampish | Date | undefined): string => {
  if (value == null || value === '') return '';
  const date = toDate(value as Timestampish);
  return date ? date.toLocaleDateString('en-US', { timeZone: 'UTC' }) : '';
};

export const formatDateTime = (value: Timestampish | Date): string => {
  const date = toDate(value as Timestampish);
  return date ? date.toLocaleString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
};

/** `M/D` for compact chips ("Sale $X until 11/1"). */
export const formatShortDate = (value: Timestampish | Date): string => {
  const date = toDate(value as Timestampish);
  return date ? `${date.getMonth() + 1}/${date.getDate()}` : '';
};

export const hostOf = (url?: string): string => {
  if (!url) return '';
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

/** Descriptions are stored as HTML; the read view shows text with paragraph breaks. */
export function htmlToParagraphs(html: string): string {
  return decodeHtmlEntities(
    html
      .replace(/<\s*br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|li|h\d)>/gi, '\n\n')
      .replace(/<[^>]+>/g, ''),
  )
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

const isEmpty = (value: unknown): boolean =>
  value == null || value === '' || (Array.isArray(value) && value.length === 0);

export type ProvenanceKey = 'description' | 'shortDescription' | 'features' | 'images' | 'specs';

const PROVENANCE_SOURCE: Record<string, StagedFieldSource['source']> = {
  scraped: 'scraped',
  official: 'official',
  ai: 'ai',
  manual: 'manual',
};

/**
 * The source badge for one field (handoff rule): a value edited away from its
 * stored value is MANUAL, an empty value is MISSING, otherwise the stored
 * origin: the manual-field lock, then field provenance. Unknown origin shows
 * no badge rather than a guess.
 */
export function fieldSource(
  product: AdminCanonicalProduct,
  value: unknown,
  stored: unknown,
  path: string,
  provenanceKey?: ProvenanceKey,
): StagedFieldSource | null {
  if (JSON.stringify(value) !== JSON.stringify(stored)) return { source: 'manual', detail: 'Edited, not saved yet' };
  if (isEmpty(value)) return { source: 'missing' };
  const lock = product.manualFields?.[path];
  if (lock) {
    return {
      source: lock.source === 'ai' ? 'ai' : 'manual',
      detail: `${lock.source === 'ai' ? 'Accepted AI suggestion' : 'Saved by an admin'}${lock.at ? ` ${formatDate(lock.at)}` : ''}; staged promotions keep it`,
    };
  }
  const entry = provenanceKey ? product.provenance?.[provenanceKey] : undefined;
  if (entry) {
    const source = PROVENANCE_SOURCE[entry.source] || 'scraped';
    return { source, confidence: entry.confidence, detail: entry.at ? `Set ${formatDate(entry.at)}` : undefined };
  }
  return null;
}

/** Badge for a pricing field from who owns it. */
export function pricingSource(
  product: AdminCanonicalProduct,
  key: string,
  value: string,
  stored: string,
): { source: StagedFieldSource | null; label?: string } {
  if (value !== stored) return { source: { source: 'manual', detail: 'Edited, not saved yet' } };
  if (!value) return { source: { source: 'missing' } };
  const lock = product.manualFields?.[`pricing.${key}`];
  if (lock) return { source: { source: lock.source === 'ai' ? 'ai' : 'manual', detail: 'Saved by an admin; staged promotions keep it' } };
  if (key === 'wholesale' || key === 'tradeCredit' || key === 'instantRebate') {
    return { source: { source: 'official', detail: 'Dealer price sheet' }, label: 'Price sheet' };
  }
  if (key === 'map' && product.pricing?.manufacturerPriceAsOf) {
    return { source: { source: 'official', detail: 'Manufacturer price file' }, label: 'Price sheet' };
  }
  if (key === 'salePrice') {
    const owner = product.pricing?.salePriceSource;
    if (owner === 'manual') return { source: { source: 'manual', detail: 'Admin sale' } };
    if (owner === 'map_rebate') return { source: { source: 'official', detail: 'MAP / rebate sheet' }, label: 'Price sheet' };
    if (owner === 'scrape') return { source: { source: 'scraped', detail: 'Manufacturer store scrape' } };
  }
  return { source: null };
}
